import { Thread } from 'app/messages/message-service/message.model';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { MessageService } from 'app/messages/message-service/message.service';
import {
  AddBorrowerToThread,
  GetThread,
  GetUserThreads,
  MarkMessageAsRead,
  MarkThreadUnread, SetActiveThread, UnsetActiveUserThread
} from 'ng2-app/modules/today/state/email/user-threads.action';
import {
  DispatchBorrowerContextActions
} from 'ng2-app/modules/today/state/user-action-items.action';
import { catchError, map, of, tap, throwError } from 'rxjs';
import { ClearBorrowerStore } from 'ng2-app/store/borrower/borrower/borrower.actions';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import {
  MarkThreadRead,
  PatchIndividualThread,
} from 'ng2-app/messages/message-store/message.actions';
import { MessageStateModel } from 'ng2-app/messages/message-store/message.state';
import { UpdateCounts } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.action';
import { UserSmsThreadsStateModel } from 'ng2-app/modules/today/state/sms/user-sms-state';

export class UserThreadsStateModel {
  threads: Thread[];
  activeThread?: Thread;
  activeThreadBorrowerId?: number;
  listLoading: boolean;
  threadLoading: boolean;
  error: any;
  total: number;
  showLoadMore: boolean;
}

@State<UserThreadsStateModel>( {
  name: 'UserThreads',
  defaults: {
    threads: [],
    listLoading: false,
    threadLoading: false,
    error: null,
    total: 0,
    showLoadMore: false,
  },
} )

@Injectable()
export class UserThreadsState {

  @Selector()
  static listLoading( state: UserThreadsStateModel ) {
    return state.listLoading;
  }

  @Selector()
  static threadLoading( state: UserThreadsStateModel ) {
    return state.threadLoading;
  }

  @Selector()
  static total( state: UserThreadsStateModel ) {
    return state.total;
  }

  @Selector()
  static activeThread( state: UserThreadsStateModel ) {
    const unreadMessageCount = state.threads.filter( thread => thread.id === state.activeThread?.id )[0]?.unreadMessageCount;
    return state.activeThread ? {
      ...state.activeThread,
      unreadMessageCount
    } : null;
  }


  @Selector()
  static threads( state: UserThreadsStateModel ) {
    return state.threads;
  }

  @Selector()
  static showLoadMore( state: UserThreadsStateModel ) {
    return state.showLoadMore;
  }

  constructor(
    private store: Store,
    private threadsService: MessageService,
  ) {}

  @Action( GetUserThreads )
  getUserThreads(
    { getState, patchState }: StateContext<UserThreadsStateModel>,
    { lenderUserId, reset, page, filterBy, sortDirection }
  ) {
    const current = getState();

    const prePatch = {
      threads: current.threads,
      listLoading: true,
    };


    if ( reset ) {
      prePatch.threads = [];
    }

    patchState( prePatch );

    return this.threadsService
      .getUserThreads( lenderUserId, page, filterBy, sortDirection )
      .pipe(
        tap( ( response ) => {

          let threads = response.data;
          threads.forEach(thread => {
            thread.mostRecentPreview = thread.mostRecentHtml?.replace(/(<[^>]*>)|\n/g, '');
          });
          const currentThreads = getState().threads;
          threads = reset ? threads :
            sortDirection === 'desc' ? [ ...currentThreads, ...threads ] :
              [ ...threads, ...currentThreads ];

          const firstThread = threads[0],
            total = response.total;
          const showLoadMore = threads.length < total;

          patchState( {
            threads,
            listLoading: false,
            total,
            showLoadMore,
          } );

          if ( reset && firstThread ) {
            this.store.dispatch( new GetThread( firstThread.id ) );
            this.store.dispatch( new MarkThreadRead(firstThread.id) );
          }

        } ),
        catchError( ( error ) => {
          patchState( {
            threads: [],
            listLoading: false,
            total: 0,
            error,
          } );
          return of( '' );
        } )
      );
  }

  @Action(GetThread)
  getThread(
    { getState, patchState }: StateContext<UserThreadsStateModel>,
    { threadId, filterUnread }: GetThread
  ){
    patchState({
      threadLoading: true,
    });

    const currentBorrowerId = getState().activeThreadBorrowerId;

    this.threadsService.getSingleThread(threadId)
      .subscribe( thread => {
        const newBorrowerId = thread.borrowerId;
        const patchVal: any = {
          activeThread: thread,
          threadLoading: false,
          activeThreadBorrowerId: thread.borrowerId,
        };
        if(filterUnread) {
          patchVal.threads = getState().threads.filter( filterThread => filterThread.unreadMessageCount );
        }
        patchState(patchVal);
        this.store.dispatch( new PatchIndividualThread(thread) );
        if(newBorrowerId && (newBorrowerId !== currentBorrowerId)) {
          this.store.dispatch( new ClearBorrowerStore() );
          this.store.dispatch( new DispatchBorrowerContextActions(newBorrowerId) );
        }
      });
  }

  @Action(MarkMessageAsRead)
  markMessageAsRead(
    { getState, setState }: StateContext<UserThreadsStateModel>,
    { message }: MarkMessageAsRead
  ) {

    const currentThread = getState().threads.filter( filterThread => filterThread.id === message.threadId )[0];
    if(currentThread) {
      setState(
        patch({
          threads: updateItem( (thread: Thread) => thread.id === message.threadId, patch({
            unreadMessageCount: --currentThread.unreadMessageCount
          }))
        })
      );
    }

  }

  @Action(MarkThreadRead)
  markThreadRead(
    { setState }: StateContext<MessageStateModel>,
    { threadId }: MarkThreadRead
  ) {
    return this.threadsService.markThreadRead(threadId).pipe(
      catchError(err => {
        if(err !== 'FORBIDDEN') {
            throw err;
        }
        return of();
            
      }), 
      map( (newCountsResponse) => {
        setState(patch({
          threads: updateItem( (thread: Thread) => thread?.id === threadId, patch({
            unreadMessageCount: 0
          }))
        }));
        this.store.dispatch( new UpdateCounts(newCountsResponse) );
      })
    );
  }

  @Action(MarkThreadUnread)
  markThreadUnread(
    { setState }: StateContext<MessageStateModel>,
    { threadId }: MarkThreadUnread
  ) {
    return this.threadsService.markThreadUnread(threadId).pipe(
      catchError(err => throwError(() => err)),
      map( (newCountsResponse) => {
        setState(patch({
          threads: updateItem( (thread: Thread) => thread?.id === threadId, patch({
            unreadMessageCount: newCountsResponse.unreadEmailCountForThread
          }))
        }));
        this.store.dispatch( new UpdateCounts(newCountsResponse) );
      })
    );
  }

  @Action(AddBorrowerToThread)
  addBorrowerToThread({ setState }: StateContext<UserThreadsStateModel>, { borrower, threadId }: AddBorrowerToThread) {

    return this.threadsService.addBorrowerToThread(borrower.id, threadId).pipe(
      catchError(() => {
        return of();
      }),
      tap(() => {
        setState(patch({
          threads: removeItem((thread: Thread) => thread.id === threadId)
        }));
      }));
  }

  @Action(UnsetActiveUserThread)
  unsetActiveUserThread(
    { patchState }: StateContext<UserSmsThreadsStateModel>,
  ) {
    patchState({
      activeThread: undefined,
      activeThreadBorrowerId: undefined,
    });
  }

  // This request comes from message.state.ts
  @Action(SetActiveThread)
  setActiveThread({ patchState }: StateContext<UserSmsThreadsStateModel>, { activeThread }: SetActiveThread) {
    patchState({ activeThread });
  }

}
